import { PencilIcon, PlusIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import { useSWRConfig } from 'swr';

import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { SimpleComboboxField } from '../../../components/combobox/SimpleComboboxField';
import { usePackageTypes, PACKAGE_TYPES_SWR_KEY } from '../models';
import { useTranslation } from '../../../contexts/translation-context';
import { getLocalisedName } from '@utils/localisation';
import { productTypeToComboboxItem } from '../productType/ProductTypeComboboxField';
import { CreatePackageTypeDialog } from './CreatePackageTypeDialog';
import { UpdatePackageTypeDialog } from './UpdatePackageTypeDialog';

export interface IMinimalPackageType {
  id: string;
  nameNl: string;
  nameFr?: string;
  nameEn?: string;
  nameDe?: string;
}

export interface IPackageTypeComboboxProps {
  name: string;
  labelText?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: () => void;
  isNullable?: boolean;
  onlySelect?: boolean;
}

export const packageTypeToComboboxItem = (val: IMinimalPackageType, lang: string) => {
  return {
    key: val.id,
    name: `${val.id} - ${getLocalisedName(val, lang)}`,
  };
};

export const PackageTypeComboboxField: React.FC<IPackageTypeComboboxProps> = (props) => {
  const { labelText, name, onlySelect, ...otherProps } = props;
  const { data } = usePackageTypes();
  const { mutate } = useSWRConfig();
  const [field, _meta, helpers] = useField({ name });
  const { lang } = useTranslation();

  const packageTypes = data ?? [];

  const items: ISimpleComboboxItem[] = useMemo(() => {
    return data ? data.map((packageType) => packageTypeToComboboxItem(packageType, lang)) : [];
  }, [packageTypes, lang]);

  const refetch = useCallback(() => {
    mutate(PACKAGE_TYPES_SWR_KEY);
  }, [mutate]);

  const value: ISimpleComboboxItem | null = field.value;
  return (
    <div>
      <div className="flex items-center">
        <SimpleComboboxField labelText={labelText ?? 'Aard'} items={items} name={name} {...otherProps} />
        {!onlySelect && (
          <div className="flex-end flex justify-end gap-2 ml-2">
            {value && (
              <UpdatePackageTypeDialog
                id={value.key}
                onSubmit={(data) => {
                  helpers.setValue(packageTypeToComboboxItem(data, lang));
                  refetch();
                }}
                triggerText={<PencilIcon className="button-icon" />}
              />
            )}

            <CreatePackageTypeDialog
              onSubmit={(data) => {
                helpers.setValue(productTypeToComboboxItem(data));
                refetch();
              }}
              triggerText={<PlusIcon className="button-icon" />}
            />
          </div>
        )}
      </div>
    </div>
  );
};
